import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type DragEvent as ReactDragEvent,
  type PointerEvent as ReactPointerEvent,
  type WheelEvent as ReactWheelEvent,
} from 'react';
import {
  AimOutlined,
  AppstoreOutlined,
  BgColorsOutlined,
  BorderOutlined,
  ClearOutlined,
  ClockCircleOutlined,
  ColumnWidthOutlined,
  CompressOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  DragOutlined,
  EditOutlined,
  ExpandOutlined,
  FontSizeOutlined,
  GatewayOutlined,
  HighlightOutlined,
  MinusOutlined,
  NodeIndexOutlined,
  PictureOutlined,
  PlusOutlined,
  RedoOutlined,
  SaveOutlined,
  ScissorOutlined,
  SelectOutlined,
  UndoOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {
  FILL_COLORS,
  STICKY_COLORS,
  STROKE_COLORS,
  TEMPLATE_DEFINITIONS,
  buildTemplate,
  cloneElements,
  createBoardDocument,
  createElement,
  findTopElementAt,
  getConnectorPoints,
  getElementBounds,
  getElementsBounds,
  hitTestElement,
  intersects,
  isBindable,
  moveElement,
  normalizeElement,
  normalizedBounds,
  smoothPath,
  uid,
  wrapText,
  type BoardView,
  type Bounds,
  type Point,
  type TemplateId,
  type WhiteboardBundle,
  type WhiteboardDocument,
  type WhiteboardElement,
  type WhiteboardElementKind,
  type WhiteboardTool,
} from './model';
import './index.css';

const STORAGE_KEY = 'devforge-free-whiteboards-v1';
const MAX_HISTORY = 60;

type ResizeHandle = 'nw' | 'ne' | 'sw' | 'se';

type Interaction =
  | { kind: 'pan'; pointerId: number; clientStart: Point; viewStart: BoardView }
  | { kind: 'draw'; pointerId: number; id: string; before: WhiteboardElement[] }
  | { kind: 'create'; pointerId: number; id: string; start: Point; before: WhiteboardElement[] }
  | { kind: 'move'; pointerId: number; start: Point; ids: string[]; before: WhiteboardElement[] }
  | { kind: 'marquee'; pointerId: number; start: Point; current: Point }
  | { kind: 'resize'; pointerId: number; start: Point; id: string; handle: ResizeHandle; original: WhiteboardElement; before: WhiteboardElement[] }
  | { kind: 'connector-end'; pointerId: number; id: string; endpoint: 'start' | 'end'; fixed: Point; before: WhiteboardElement[] }
  | { kind: 'erase'; pointerId: number; before: WhiteboardElement[]; changed: boolean };

const TOOL_META: Record<WhiteboardTool, { label: string; shortcut?: string }> = {
  select: { label: '选择', shortcut: 'V' },
  hand: { label: '移动画布', shortcut: 'H' },
  pen: { label: '画笔', shortcut: 'P' },
  highlighter: { label: '荧光笔', shortcut: 'K' },
  eraser: { label: '橡皮擦', shortcut: 'E' },
  text: { label: '文本', shortcut: 'T' },
  sticky: { label: '便签', shortcut: 'S' },
  rectangle: { label: '矩形', shortcut: 'R' },
  ellipse: { label: '圆形', shortcut: 'O' },
  diamond: { label: '菱形', shortcut: 'D' },
  arrow: { label: '连线', shortcut: 'L' },
  frame: { label: '讨论区', shortcut: 'F' },
};

const TEXT_KINDS: WhiteboardElementKind[] = ['text', 'sticky', 'rectangle', 'ellipse', 'diamond', 'frame'];
const FILLABLE_KINDS: WhiteboardElementKind[] = ['sticky', 'rectangle', 'ellipse', 'diamond', 'frame'];

function defaultBoardTitle() {
  const date = new Date();
  return `会议画板 · ${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
}

function readBundle(): WhiteboardBundle {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as WhiteboardBundle;
      if (Array.isArray(parsed.boards) && parsed.boards.length > 0) {
        const current = parsed.boards.find((board) => board.id === parsed.currentId) || parsed.boards[0];
        return {
          currentId: current.id,
          boards: parsed.boards.map((board) => ({
            ...board,
            title: board.title || '未命名画板',
            elements: Array.isArray(board.elements) ? board.elements : [],
            view: board.view || { x: 0, y: 0, scale: 1 },
          })),
        };
      }
    }
  } catch {
    // 本地数据损坏时回到一张安全的空白画板。
  }
  const board = createBoardDocument(defaultBoardTitle());
  return { currentId: board.id, boards: [board] };
}

function isTypingTarget(target: EventTarget | null) {
  const node = target as HTMLElement | null;
  return Boolean(node?.closest('input, textarea, [contenteditable="true"]'));
}

function snapshotsEqual(a: WhiteboardElement[], b: WhiteboardElement[]) {
  if (a.length !== b.length) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

function removeElements(source: WhiteboardElement[], ids: Set<string>): WhiteboardElement[] {
  return source
    .filter((element) => !ids.has(element.id))
    .map((element) => {
      if (element.kind !== 'arrow' || (!ids.has(element.startBinding || '') && !ids.has(element.endBinding || ''))) return element;
      const points = getConnectorPoints(element, source);
      return {
        ...element,
        x: points.start.x,
        y: points.start.y,
        width: points.end.x - points.start.x,
        height: points.end.y - points.start.y,
        startBinding: ids.has(element.startBinding || '') ? undefined : element.startBinding,
        endBinding: ids.has(element.endBinding || '') ? undefined : element.endBinding,
      };
    });
}

function arrowHeadPoints(start: Point, end: Point, size: number): string {
  const angle = Math.atan2(end.y - start.y, end.x - start.x);
  const left = {
    x: end.x - Math.cos(angle - Math.PI / 6) * size,
    y: end.y - Math.sin(angle - Math.PI / 6) * size,
  };
  const right = {
    x: end.x - Math.cos(angle + Math.PI / 6) * size,
    y: end.y - Math.sin(angle + Math.PI / 6) * size,
  };
  return `${end.x},${end.y} ${left.x},${left.y} ${right.x},${right.y}`;
}

function ElementText({ element }: { element: WhiteboardElement }) {
  if (!element.text || !TEXT_KINDS.includes(element.kind)) return null;
  const isPlainText = element.kind === 'text';
  const isSticky = element.kind === 'sticky';
  const maxWidth = Math.max(20, element.width - (isSticky ? 32 : 24));
  const maxLines = Math.max(1, Math.floor((element.height - (isSticky ? 36 : 18)) / (element.fontSize * 1.28)));
  const lines = wrapText(element.text, maxWidth, element.fontSize, maxLines);
  const lineHeight = element.fontSize * 1.28;
  const startY = isPlainText
    ? element.y + element.fontSize
    : isSticky
      ? element.y + 38
      : element.y + element.height / 2 - ((lines.length - 1) * lineHeight) / 2;
  const textX = isPlainText || isSticky ? element.x + (isPlainText ? 2 : 18) : element.x + element.width / 2;

  return (
    <text
      x={textX}
      y={startY}
      fill={element.stroke}
      fontSize={element.fontSize}
      fontFamily="Inter, 'PingFang SC', 'Microsoft YaHei', sans-serif"
      fontWeight={isPlainText ? 620 : 650}
      textAnchor={isPlainText || isSticky ? 'start' : 'middle'}
      dominantBaseline={isPlainText || isSticky ? 'auto' : 'middle'}
      pointerEvents="none"
    >
      {lines.map((line, index) => (
        <tspan key={`${index}-${line}`} x={textX} dy={index === 0 ? 0 : lineHeight}>{line || ' '}</tspan>
      ))}
    </text>
  );
}

function BoardElementView({ element, allElements }: { element: WhiteboardElement; allElements: WhiteboardElement[] }) {
  const common = { opacity: element.opacity, 'data-element-id': element.id };

  if (element.kind === 'path' || element.kind === 'highlighter') {
    const path = smoothPath(element.points);
    return (
      <g {...common}>
        <path d={path} fill="none" stroke="transparent" strokeWidth={Math.max(16, element.strokeWidth + 10)} strokeLinecap="round" strokeLinejoin="round" />
        <path d={path} fill="none" stroke={element.stroke} strokeWidth={element.strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
      </g>
    );
  }

  if (element.kind === 'arrow') {
    const { start, end } = getConnectorPoints(element, allElements);
    return (
      <g {...common}>
        <line x1={start.x} y1={start.y} x2={end.x} y2={end.y} stroke="transparent" strokeWidth={Math.max(18, element.strokeWidth + 12)} />
        <line x1={start.x} y1={start.y} x2={end.x} y2={end.y} stroke={element.stroke} strokeWidth={element.strokeWidth} strokeLinecap="round" />
        <polygon points={arrowHeadPoints(start, end, 11 + element.strokeWidth)} fill={element.stroke} />
      </g>
    );
  }

  if (element.kind === 'image') {
    return (
      <g {...common}>
        <rect x={element.x} y={element.y} width={element.width} height={element.height} rx={12} fill="#fff" filter="url(#wb-soft-shadow)" />
        <image href={element.src} x={element.x} y={element.y} width={element.width} height={element.height} preserveAspectRatio="xMidYMid meet" />
      </g>
    );
  }

  if (element.kind === 'frame') {
    return (
      <g {...common}>
        <rect
          x={element.x}
          y={element.y}
          width={element.width}
          height={element.height}
          rx={18}
          fill={element.fill}
          stroke={element.stroke}
          strokeWidth={element.strokeWidth}
          strokeDasharray="9 7"
          style={{ pointerEvents: 'stroke' }}
        />
        <text x={element.x + 10} y={element.y - 13} fill={element.stroke} fontSize={element.fontSize} fontWeight={750} fontFamily="Inter, 'PingFang SC', sans-serif" pointerEvents="none">
          {element.text || '讨论区'}
        </text>
      </g>
    );
  }

  if (element.kind === 'text') {
    return <g {...common}><ElementText element={element} /></g>;
  }

  return (
    <g {...common} filter={element.kind === 'sticky' ? 'url(#wb-soft-shadow)' : undefined}>
      {element.kind === 'rectangle' && (
        <rect x={element.x} y={element.y} width={element.width} height={element.height} rx={16} fill={element.fill} stroke={element.stroke} strokeWidth={element.strokeWidth} />
      )}
      {element.kind === 'ellipse' && (
        <ellipse cx={element.x + element.width / 2} cy={element.y + element.height / 2} rx={Math.max(1, element.width / 2)} ry={Math.max(1, element.height / 2)} fill={element.fill} stroke={element.stroke} strokeWidth={element.strokeWidth} />
      )}
      {element.kind === 'diamond' && (
        <polygon
          points={`${element.x + element.width / 2},${element.y} ${element.x + element.width},${element.y + element.height / 2} ${element.x + element.width / 2},${element.y + element.height} ${element.x},${element.y + element.height / 2}`}
          fill={element.fill}
          stroke={element.stroke}
          strokeWidth={element.strokeWidth}
          strokeLinejoin="round"
        />
      )}
      {element.kind === 'sticky' && (
        <>
          <rect x={element.x} y={element.y} width={element.width} height={element.height} rx={8} fill={element.fill} stroke="rgba(15,23,42,.12)" strokeWidth={1} />
          <path d={`M ${element.x + element.width - 28} ${element.y} L ${element.x + element.width} ${element.y + 28} L ${element.x + element.width} ${element.y} Z`} fill="rgba(255,255,255,.42)" />
        </>
      )}
      <ElementText element={element} />
    </g>
  );
}

function ToolButton({ tool, current, icon, onClick }: { tool: WhiteboardTool; current: WhiteboardTool; icon: React.ReactNode; onClick: () => void }) {
  const meta = TOOL_META[tool];
  return (
    <button
      type="button"
      className={`wb-tool-button ${current === tool ? 'is-active' : ''}`}
      onClick={onClick}
      title={`${meta.label}${meta.shortcut ? ` (${meta.shortcut})` : ''}`}
      aria-label={meta.label}
      aria-pressed={current === tool}
    >
      {icon}
      {meta.shortcut && <kbd>{meta.shortcut}</kbd>}
    </button>
  );
}

export default function FreeWhiteboardPage() {
  const initialBundleRef = useRef<WhiteboardBundle | null>(null);
  if (!initialBundleRef.current) initialBundleRef.current = readBundle();
  const initialBundle = initialBundleRef.current;
  const initialBoard = initialBundle.boards.find((board) => board.id === initialBundle.currentId) || initialBundle.boards[0];

  const pageRef = useRef<HTMLDivElement>(null);
  const workspaceRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const backupInputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<HTMLTextAreaElement>(null);
  const boardsRef = useRef<WhiteboardDocument[]>(initialBundle.boards);
  const elementsRef = useRef<WhiteboardElement[]>(cloneElements(initialBoard.elements));
  const titleRef = useRef(initialBoard.title);
  const viewRef = useRef<BoardView>({ ...initialBoard.view });
  const interactionRef = useRef<Interaction | null>(null);
  const spaceHeldRef = useRef(false);
  const editingRef = useRef<string | null>(null);
  const editBeforeRef = useRef<WhiteboardElement[] | null>(null);
  const internalClipboardRef = useRef<WhiteboardElement[]>([]);
  const saveTimerRef = useRef<number | null>(null);

  const [boards, setBoards] = useState<WhiteboardDocument[]>(initialBundle.boards);
  const [boardId, setBoardId] = useState(initialBoard.id);
  const [title, setTitle] = useState(initialBoard.title);
  const [elements, setElements] = useState<WhiteboardElement[]>(cloneElements(initialBoard.elements));
  const [view, setView] = useState<BoardView>({ ...initialBoard.view });
  const [tool, setTool] = useState<WhiteboardTool>('select');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [undoStack, setUndoStack] = useState<WhiteboardElement[][]>([]);
  const [redoStack, setRedoStack] = useState<WhiteboardElement[][]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [marquee, setMarquee] = useState<Bounds | null>(null);
  const [spaceActive, setSpaceActive] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving'>('saved');
  const [boardMenuOpen, setBoardMenuOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [timerOpen, setTimerOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [shapeMenuOpen, setShapeMenuOpen] = useState(false);
  const [colorMenuOpen, setColorMenuOpen] = useState(false);
  const [welcomeDismissed, setWelcomeDismissed] = useState(initialBoard.elements.length > 0);
  const [gridVisible, setGridVisible] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(5 * 60);
  const [timerRunning, setTimerRunning] = useState(false);
  const [draftStyle, setDraftStyle] = useState({ stroke: '#172033', fill: '#ffffff', strokeWidth: 2 });

  elementsRef.current = elements;
  titleRef.current = title;
  viewRef.current = view;
  boardsRef.current = boards;
  editingRef.current = editingId;

  const setElementsNow = useCallback((next: WhiteboardElement[]) => {
    elementsRef.current = next;
    setElements(next);
  }, []);

  const setViewNow = useCallback((next: BoardView) => {
    const normalized = { ...next, scale: Math.max(0.2, Math.min(3, next.scale)) };
    viewRef.current = normalized;
    setView(normalized);
  }, []);

  const recordCommit = useCallback((before: WhiteboardElement[], next = elementsRef.current) => {
    if (snapshotsEqual(before, next)) return;
    setUndoStack((stack) => [...stack, cloneElements(before)].slice(-MAX_HISTORY));
    setRedoStack([]);
  }, []);

  const commitElements = useCallback((next: WhiteboardElement[], before = elementsRef.current) => {
    setElementsNow(cloneElements(next));
    recordCommit(before, next);
  }, [recordCommit, setElementsNow]);

  const persistCurrent = useCallback((currentId = boardId) => {
    const updatedAt = Date.now();
    const snapshot: WhiteboardDocument = {
      id: currentId,
      title: titleRef.current.trim() || '未命名画板',
      elements: cloneElements(elementsRef.current),
      view: { ...viewRef.current },
      updatedAt,
    };
    let nextBoards = boardsRef.current.map((board) => board.id === currentId ? snapshot : board);
    if (!nextBoards.some((board) => board.id === currentId)) nextBoards = [...nextBoards, snapshot];
    boardsRef.current = nextBoards;
    setBoards(nextBoards);
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ currentId, boards: nextBoards } satisfies WhiteboardBundle));
    return nextBoards;
  }, [boardId]);

  useEffect(() => {
    setSaveStatus('saving');
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(() => {
      persistCurrent();
      setSaveStatus('saved');
    }, 450);
    return () => {
      if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    };
  }, [elements, title, view, persistCurrent]);

  useEffect(() => {
    if (!timerRunning) return undefined;
    const interval = window.setInterval(() => {
      setTimerSeconds((seconds) => {
        if (seconds <= 1) {
          setTimerRunning(false);
          return 0;
        }
        return seconds - 1;
      });
    }, 1000);
    return () => window.clearInterval(interval);
  }, [timerRunning]);

  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(document.fullscreenElement === pageRef.current);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const selectedElements = useMemo(
    () => selectedIds.map((id) => elements.find((element) => element.id === id)).filter(Boolean) as WhiteboardElement[],
    [elements, selectedIds],
  );
  const selectedBounds = useMemo(() => getElementsBounds(selectedElements), [selectedElements]);

  const hidePanels = useCallback(() => {
    setBoardMenuOpen(false);
    setTemplateOpen(false);
    setTimerOpen(false);
    setExportOpen(false);
    setShapeMenuOpen(false);
    setColorMenuOpen(false);
  }, []);

  const chooseTool = useCallback((next: WhiteboardTool) => {
    setTool(next);
    setShapeMenuOpen(false);
    setColorMenuOpen(false);
    if (next !== 'select') setEditingId(null);
  }, []);

  const eventToBoardPoint = useCallback((clientX: number, clientY: number): Point => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    const currentView = viewRef.current;
    return {
      x: (clientX - rect.left - currentView.x) / currentView.scale,
      y: (clientY - rect.top - currentView.y) / currentView.scale,
    };
  }, []);

  const beginTextEdit = useCallback((id: string) => {
    const element = elementsRef.current.find((item) => item.id === id);
    if (!element || !TEXT_KINDS.includes(element.kind)) return;
    editBeforeRef.current = cloneElements(elementsRef.current);
    editingRef.current = id;
    setEditingId(id);
    setSelectedIds([id]);
    window.requestAnimationFrame(() => {
      editorRef.current?.focus();
      editorRef.current?.select();
    });
  }, []);

  const finishTextEdit = useCallback((revert = false) => {
    const before = editBeforeRef.current;
    editBeforeRef.current = null;
    editingRef.current = null;
    setEditingId(null);
    if (!before) return;
    if (revert) setElementsNow(before);
    else recordCommit(before, elementsRef.current);
  }, [recordCommit, setElementsNow]);

  const undo = useCallback(() => {
    setUndoStack((stack) => {
      if (!stack.length) return stack;
      const previous = stack[stack.length - 1];
      setRedoStack((redo) => [cloneElements(elementsRef.current), ...redo].slice(0, MAX_HISTORY));
      setElementsNow(cloneElements(previous));
      setSelectedIds([]);
      return stack.slice(0, -1);
    });
  }, [setElementsNow]);

  const redo = useCallback(() => {
    setRedoStack((stack) => {
      if (!stack.length) return stack;
      const next = stack[0];
      setUndoStack((undoItems) => [...undoItems, cloneElements(elementsRef.current)].slice(-MAX_HISTORY));
      setElementsNow(cloneElements(next));
      setSelectedIds([]);
      return stack.slice(1);
    });
  }, [setElementsNow]);

  const deleteSelected = useCallback(() => {
    if (!selectedIds.length) return;
    const before = cloneElements(elementsRef.current);
    const next = removeElements(elementsRef.current, new Set(selectedIds));
    commitElements(next, before);
    setSelectedIds([]);
  }, [commitElements, selectedIds]);

  const copySelected = useCallback(() => {
    internalClipboardRef.current = cloneElements(selectedElements);
  }, [selectedElements]);

  const pasteInternal = useCallback(() => {
    if (!internalClipboardRef.current.length) return;
    const before = cloneElements(elementsRef.current);
    const idMap = new Map<string, string>();
    internalClipboardRef.current.forEach((element) => idMap.set(element.id, uid(element.kind)));
    const pasted = internalClipboardRef.current.map((element) => {
      const moved = moveElement(element, 28, 28);
      return {
        ...moved,
        id: idMap.get(element.id)!,
        startBinding: element.startBinding ? idMap.get(element.startBinding) : undefined,
        endBinding: element.endBinding ? idMap.get(element.endBinding) : undefined,
      };
    });
    commitElements([...before, ...pasted], before);
    setSelectedIds(pasted.map((element) => element.id));
    internalClipboardRef.current = cloneElements(pasted);
  }, [commitElements]);

  const duplicateSelected = useCallback(() => {
    copySelected();
    pasteInternal();
  }, [copySelected, pasteInternal]);

  const patchSelected = useCallback((patch: Partial<WhiteboardElement>) => {
    if (!selectedIds.length) return;
    const before = cloneElements(elementsRef.current);
    const ids = new Set(selectedIds);
    const next = elementsRef.current.map((element) => ids.has(element.id) ? { ...element, ...patch } : element);
    commitElements(next, before);
  }, [commitElements, selectedIds]);

  const moveSelectionLayer = useCallback((direction: 'front' | 'back') => {
    if (!selectedIds.length) return;
    const before = cloneElements(elementsRef.current);
    const ids = new Set(selectedIds);
    const selected = elementsRef.current.filter((element) => ids.has(element.id));
    const rest = elementsRef.current.filter((element) => !ids.has(element.id));
    commitElements(direction === 'front' ? [...rest, ...selected] : [...selected, ...rest], before);
  }, [commitElements, selectedIds]);

  const switchBoard = useCallback((nextId: string) => {
    if (nextId === boardId) {
      setBoardMenuOpen(false);
      return;
    }
    persistCurrent();
    const target = boardsRef.current.find((board) => board.id === nextId);
    if (!target) return;
    setBoardId(target.id);
    titleRef.current = target.title;
    setTitle(target.title);
    setElementsNow(cloneElements(target.elements));
    setViewNow({ ...target.view });
    setSelectedIds([]);
    setUndoStack([]);
    setRedoStack([]);
    setWelcomeDismissed(target.elements.length > 0);
    setBoardMenuOpen(false);
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ currentId: target.id, boards: boardsRef.current } satisfies WhiteboardBundle));
  }, [boardId, persistCurrent, setElementsNow, setViewNow]);

  const createNewBoard = useCallback(() => {
    const saved = persistCurrent();
    const board = createBoardDocument(defaultBoardTitle());
    const nextBoards = [board, ...saved];
    boardsRef.current = nextBoards;
    setBoards(nextBoards);
    setBoardId(board.id);
    titleRef.current = board.title;
    setTitle(board.title);
    setElementsNow([]);
    setViewNow({ x: 0, y: 0, scale: 1 });
    setSelectedIds([]);
    setUndoStack([]);
    setRedoStack([]);
    setWelcomeDismissed(false);
    setBoardMenuOpen(false);
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ currentId: board.id, boards: nextBoards } satisfies WhiteboardBundle));
  }, [persistCurrent, setElementsNow, setViewNow]);

  const deleteBoard = useCallback((id: string) => {
    const target = boardsRef.current.find((board) => board.id === id);
    if (!target || !window.confirm(`确定删除“${target.title}”吗？此操作仅删除当前浏览器中的画板。`)) return;
    if (boardsRef.current.length === 1) {
      const before = cloneElements(elementsRef.current);
      commitElements([], before);
      setTitle(defaultBoardTitle());
      setBoardMenuOpen(false);
      return;
    }
    const nextBoards = boardsRef.current.filter((board) => board.id !== id);
    boardsRef.current = nextBoards;
    setBoards(nextBoards);
    if (id === boardId) {
      const next = nextBoards[0];
      setBoardId(next.id);
      setTitle(next.title);
      setElementsNow(cloneElements(next.elements));
      setViewNow({ ...next.view });
      setSelectedIds([]);
      setUndoStack([]);
      setRedoStack([]);
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ currentId: id === boardId ? nextBoards[0].id : boardId, boards: nextBoards } satisfies WhiteboardBundle));
  }, [boardId, commitElements, setElementsNow, setViewNow]);

  const viewportCenter = useCallback((): Point => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return eventToBoardPoint(rect.left + rect.width / 2, rect.top + rect.height / 2);
  }, [eventToBoardPoint]);

  const insertTemplate = useCallback((templateId: TemplateId) => {
    const before = cloneElements(elementsRef.current);
    const template = buildTemplate(templateId, viewportCenter());
    commitElements([...before, ...template], before);
    setSelectedIds(template.map((element) => element.id));
    setWelcomeDismissed(true);
    setTemplateOpen(false);
    setTool('select');
  }, [commitElements, viewportCenter]);

  const fitToContent = useCallback(() => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const bounds = getElementsBounds(elementsRef.current);
    if (!bounds) {
      setViewNow({ x: 0, y: 0, scale: 1 });
      return;
    }
    const scale = Math.max(0.2, Math.min(1.35, (rect.width - 180) / (bounds.width + 100), (rect.height - 140) / (bounds.height + 100)));
    setViewNow({
      scale,
      x: rect.width / 2 - (bounds.x + bounds.width / 2) * scale,
      y: rect.height / 2 - (bounds.y + bounds.height / 2) * scale,
    });
  }, [setViewNow]);

  const zoomAtCenter = useCallback((factor: number) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const current = viewRef.current;
    const nextScale = Math.max(0.2, Math.min(3, current.scale * factor));
    const boardCenter = {
      x: (rect.width / 2 - current.x) / current.scale,
      y: (rect.height / 2 - current.y) / current.scale,
    };
    setViewNow({
      scale: nextScale,
      x: rect.width / 2 - boardCenter.x * nextScale,
      y: rect.height / 2 - boardCenter.y * nextScale,
    });
  }, [setViewNow]);

  const onWheel = useCallback((event: ReactWheelEvent<SVGSVGElement>) => {
    event.preventDefault();
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const current = viewRef.current;
    if (event.ctrlKey || event.metaKey) {
      const factor = Math.exp(-event.deltaY * 0.002);
      const nextScale = Math.max(0.2, Math.min(3, current.scale * factor));
      const local = { x: event.clientX - rect.left, y: event.clientY - rect.top };
      const boardPoint = { x: (local.x - current.x) / current.scale, y: (local.y - current.y) / current.scale };
      setViewNow({ scale: nextScale, x: local.x - boardPoint.x * nextScale, y: local.y - boardPoint.y * nextScale });
    } else {
      setViewNow({ ...current, x: current.x - event.deltaX, y: current.y - event.deltaY });
    }
  }, [setViewNow]);

  const eraseAtPoint = useCallback((point: Point) => {
    const hit = findTopElementAt(point, elementsRef.current);
    if (!hit) return false;
    setElementsNow(removeElements(elementsRef.current, new Set([hit.id])));
    setSelectedIds((ids) => ids.filter((id) => id !== hit.id));
    return true;
  }, [setElementsNow]);

  const onPointerDown = useCallback((event: ReactPointerEvent<SVGSVGElement>) => {
    if (event.button !== 0 && event.button !== 1) return;
    if (editingRef.current) finishTextEdit();
    hidePanels();
    setWelcomeDismissed(true);
    svgRef.current?.focus({ preventScroll: true });
    svgRef.current?.setPointerCapture(event.pointerId);
    const point = eventToBoardPoint(event.clientX, event.clientY);
    const targetId = (event.target as SVGElement).closest?.('[data-element-id]')?.getAttribute('data-element-id') || undefined;
    const target = targetId ? elementsRef.current.find((element) => element.id === targetId) : findTopElementAt(point, elementsRef.current);

    if (event.button === 1 || tool === 'hand' || spaceHeldRef.current) {
      interactionRef.current = {
        kind: 'pan',
        pointerId: event.pointerId,
        clientStart: { x: event.clientX, y: event.clientY },
        viewStart: { ...viewRef.current },
      };
      return;
    }

    const before = cloneElements(elementsRef.current);
    if (tool === 'eraser') {
      interactionRef.current = { kind: 'erase', pointerId: event.pointerId, before, changed: eraseAtPoint(point) };
      return;
    }

    if (tool === 'pen' || tool === 'highlighter') {
      const kind = tool === 'pen' ? 'path' : 'highlighter';
      const created = createElement(kind, point.x, point.y, {
        stroke: draftStyle.stroke,
        strokeWidth: tool === 'pen' ? Math.max(2, draftStyle.strokeWidth + 1) : 18,
        opacity: tool === 'pen' ? 1 : 0.32,
      });
      setElementsNow([...before, created]);
      setSelectedIds([]);
      interactionRef.current = { kind: 'draw', pointerId: event.pointerId, id: created.id, before };
      return;
    }

    if (tool === 'arrow') {
      const created = createElement('arrow', point.x, point.y, {
        width: 0,
        height: 0,
        stroke: draftStyle.stroke,
        strokeWidth: draftStyle.strokeWidth,
        startBinding: isBindable(target) ? target.id : undefined,
      });
      setElementsNow([...before, created]);
      setSelectedIds([created.id]);
      interactionRef.current = { kind: 'create', pointerId: event.pointerId, id: created.id, start: point, before };
      return;
    }

    const creationKind: Partial<Record<WhiteboardTool, WhiteboardElementKind>> = {
      text: 'text',
      sticky: 'sticky',
      rectangle: 'rectangle',
      ellipse: 'ellipse',
      diamond: 'diamond',
      frame: 'frame',
    };
    const kind = creationKind[tool];
    if (kind) {
      const created = createElement(kind, point.x, point.y, {
        width: 0,
        height: 0,
        stroke: draftStyle.stroke,
        fill: kind === 'sticky' ? '#fef3a6' : kind === 'text' ? 'transparent' : draftStyle.fill,
        strokeWidth: kind === 'text' ? 0 : draftStyle.strokeWidth,
      });
      setElementsNow([...before, created]);
      setSelectedIds([created.id]);
      interactionRef.current = { kind: 'create', pointerId: event.pointerId, id: created.id, start: point, before };
      return;
    }

    if (target) {
      let ids = selectedIds;
      if (event.shiftKey) {
        if (selectedIds.includes(target.id)) {
          const next = selectedIds.filter((id) => id !== target.id);
          setSelectedIds(next);
          return;
        }
        ids = [...selectedIds, target.id];
      } else if (!selectedIds.includes(target.id)) {
        ids = [target.id];
      }
      setSelectedIds(ids);
      interactionRef.current = { kind: 'move', pointerId: event.pointerId, start: point, ids, before };
      return;
    }

    if (!event.shiftKey) setSelectedIds([]);
    interactionRef.current = { kind: 'marquee', pointerId: event.pointerId, start: point, current: point };
    setMarquee({ x: point.x, y: point.y, width: 0, height: 0 });
  }, [draftStyle, eraseAtPoint, eventToBoardPoint, finishTextEdit, hidePanels, selectedIds, setElementsNow, tool]);

  const onPointerMove = useCallback((event: ReactPointerEvent<SVGSVGElement>) => {
    const action = interactionRef.current;
    if (!action || action.pointerId !== event.pointerId) return;
    if (action.kind === 'pan') {
      setViewNow({
        ...action.viewStart,
        x: action.viewStart.x + event.clientX - action.clientStart.x,
        y: action.viewStart.y + event.clientY - action.clientStart.y,
      });
      return;
    }

    const point = eventToBoardPoint(event.clientX, event.clientY);
    if (action.kind === 'draw') {
      const target = elementsRef.current.find((element) => element.id === action.id);
      const last = target?.points?.[target.points.length - 1];
      if (last && Math.hypot(point.x - last.x, point.y - last.y) < 1.2 / viewRef.current.scale) return;
      setElementsNow(elementsRef.current.map((element) => element.id === action.id ? { ...element, points: [...(element.points || []), point] } : element));
      return;
    }
    if (action.kind === 'create') {
      setElementsNow(elementsRef.current.map((element) => {
        if (element.id !== action.id) return element;
        if (element.kind === 'arrow') return { ...element, width: point.x - action.start.x, height: point.y - action.start.y };
        const bounds = normalizedBounds(action.start.x, action.start.y, point.x - action.start.x, point.y - action.start.y);
        return { ...element, ...bounds };
      }));
      return;
    }
    if (action.kind === 'move') {
      const dx = point.x - action.start.x;
      const dy = point.y - action.start.y;
      const ids = new Set(action.ids);
      setElementsNow(action.before.map((element) => ids.has(element.id) ? moveElement(element, dx, dy) : element));
      return;
    }
    if (action.kind === 'marquee') {
      action.current = point;
      setMarquee(normalizedBounds(action.start.x, action.start.y, point.x - action.start.x, point.y - action.start.y));
      return;
    }
    if (action.kind === 'resize') {
      const dx = point.x - action.start.x;
      const dy = point.y - action.start.y;
      const original = action.original;
      let x = original.x;
      let y = original.y;
      let width = original.width;
      let height = original.height;
      if (action.handle.includes('w')) { x = original.x + dx; width = original.width - dx; }
      if (action.handle.includes('e')) width = original.width + dx;
      if (action.handle.includes('n')) { y = original.y + dy; height = original.height - dy; }
      if (action.handle.includes('s')) height = original.height + dy;
      if (width < 32) { if (action.handle.includes('w')) x -= 32 - width; width = 32; }
      if (height < 28) { if (action.handle.includes('n')) y -= 28 - height; height = 28; }
      setElementsNow(elementsRef.current.map((element) => element.id === action.id ? { ...element, x, y, width, height } : element));
      return;
    }
    if (action.kind === 'connector-end') {
      setElementsNow(elementsRef.current.map((element) => {
        if (element.id !== action.id) return element;
        if (action.endpoint === 'start') {
          return { ...element, x: point.x, y: point.y, width: action.fixed.x - point.x, height: action.fixed.y - point.y, startBinding: undefined };
        }
        return { ...element, width: point.x - action.fixed.x, height: point.y - action.fixed.y, endBinding: undefined };
      }));
      return;
    }
    if (action.kind === 'erase' && eraseAtPoint(point)) action.changed = true;
  }, [eraseAtPoint, eventToBoardPoint, setElementsNow, setViewNow]);

  const defaultSizeFor = (kind: WhiteboardElementKind) => {
    const sizes: Partial<Record<WhiteboardElementKind, [number, number]>> = {
      text: [220, 62],
      sticky: [190, 160],
      rectangle: [190, 108],
      ellipse: [180, 108],
      diamond: [180, 122],
      frame: [520, 340],
    };
    return sizes[kind] || [180, 100];
  };

  const finishPointerInteraction = useCallback((event: ReactPointerEvent<SVGSVGElement>) => {
    const action = interactionRef.current;
    if (!action || action.pointerId !== event.pointerId) return;
    interactionRef.current = null;
    try { svgRef.current?.releasePointerCapture(event.pointerId); } catch { /* pointer capture may already be released */ }
    const point = eventToBoardPoint(event.clientX, event.clientY);

    if (action.kind === 'create') {
      let shouldEdit = false;
      let next = elementsRef.current.map((element) => {
        if (element.id !== action.id) return element;
        if (element.kind === 'arrow') {
          const tiny = Math.hypot(element.width, element.height) < 12;
          const endPoint = tiny ? { x: action.start.x + 180, y: action.start.y } : point;
          const endTarget = findTopElementAt(endPoint, elementsRef.current, true, element.id);
          return {
            ...element,
            width: endPoint.x - action.start.x,
            height: endPoint.y - action.start.y,
            endBinding: endTarget && endTarget.id !== element.startBinding ? endTarget.id : undefined,
          };
        }
        const tiny = element.width < 12 && element.height < 12;
        if (tiny) {
          const [width, height] = defaultSizeFor(element.kind);
          shouldEdit = TEXT_KINDS.includes(element.kind) && element.kind !== 'frame';
          return { ...element, x: action.start.x, y: action.start.y, width, height };
        }
        shouldEdit = TEXT_KINDS.includes(element.kind) && element.kind !== 'frame';
        return normalizeElement(element);
      });
      setElementsNow(next);
      recordCommit(action.before, next);
      setTool('select');
      if (shouldEdit) window.requestAnimationFrame(() => beginTextEdit(action.id));
      return;
    }
    if (action.kind === 'draw' || action.kind === 'move' || action.kind === 'resize') {
      recordCommit(action.before, elementsRef.current);
      return;
    }
    if (action.kind === 'connector-end') {
      const target = findTopElementAt(point, elementsRef.current, true, action.id);
      const next = elementsRef.current.map((element) => {
        if (element.id !== action.id || element.kind !== 'arrow') return element;
        if (action.endpoint === 'start') return { ...element, startBinding: target && target.id !== element.endBinding ? target.id : undefined };
        return { ...element, endBinding: target && target.id !== element.startBinding ? target.id : undefined };
      });
      setElementsNow(next);
      recordCommit(action.before, next);
      return;
    }
    if (action.kind === 'erase') {
      if (action.changed) recordCommit(action.before, elementsRef.current);
      return;
    }
    if (action.kind === 'marquee') {
      const bounds = normalizedBounds(action.start.x, action.start.y, action.current.x - action.start.x, action.current.y - action.start.y);
      const hits = elementsRef.current.filter((element) => intersects(bounds, getElementBounds(element, elementsRef.current))).map((element) => element.id);
      setSelectedIds((current) => event.shiftKey ? Array.from(new Set([...current, ...hits])) : hits);
      setMarquee(null);
    }
  }, [beginTextEdit, eventToBoardPoint, recordCommit, setElementsNow]);

  const onDoubleClick = useCallback((event: ReactPointerEvent<SVGSVGElement>) => {
    const point = eventToBoardPoint(event.clientX, event.clientY);
    const targetId = (event.target as SVGElement).closest?.('[data-element-id]')?.getAttribute('data-element-id');
    const target = targetId ? elementsRef.current.find((element) => element.id === targetId) : findTopElementAt(point, elementsRef.current);
    if (target && TEXT_KINDS.includes(target.kind)) {
      beginTextEdit(target.id);
      return;
    }
    if (!target) {
      const before = cloneElements(elementsRef.current);
      const text = createElement('text', point.x, point.y, { stroke: draftStyle.stroke });
      commitElements([...before, text], before);
      setTool('select');
      window.requestAnimationFrame(() => beginTextEdit(text.id));
    }
  }, [beginTextEdit, commitElements, draftStyle.stroke, eventToBoardPoint]);

  const startResize = useCallback((event: ReactPointerEvent<SVGCircleElement>, element: WhiteboardElement, handle: ResizeHandle) => {
    event.stopPropagation();
    svgRef.current?.setPointerCapture(event.pointerId);
    interactionRef.current = {
      kind: 'resize',
      pointerId: event.pointerId,
      start: eventToBoardPoint(event.clientX, event.clientY),
      id: element.id,
      handle,
      original: { ...element },
      before: cloneElements(elementsRef.current),
    };
  }, [eventToBoardPoint]);

  const startConnectorEndpoint = useCallback((event: ReactPointerEvent<SVGCircleElement>, element: WhiteboardElement, endpoint: 'start' | 'end') => {
    event.stopPropagation();
    svgRef.current?.setPointerCapture(event.pointerId);
    const points = getConnectorPoints(element, elementsRef.current);
    interactionRef.current = {
      kind: 'connector-end',
      pointerId: event.pointerId,
      id: element.id,
      endpoint,
      fixed: endpoint === 'start' ? points.end : points.start,
      before: cloneElements(elementsRef.current),
    };
  }, []);

  const importImage = useCallback((file: File, atPoint?: Point) => {
    if (!file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => {
      const src = String(reader.result || '');
      const image = new window.Image();
      image.onload = () => {
        const maxWidth = 520;
        const maxHeight = 360;
        const ratio = Math.min(1, maxWidth / Math.max(1, image.width), maxHeight / Math.max(1, image.height));
        const width = Math.max(80, image.width * ratio);
        const height = Math.max(60, image.height * ratio);
        const center = atPoint || viewportCenter();
        const created = createElement('image', center.x - width / 2, center.y - height / 2, { width, height, src });
        const before = cloneElements(elementsRef.current);
        commitElements([...before, created], before);
        setSelectedIds([created.id]);
        setTool('select');
        setWelcomeDismissed(true);
      };
      image.src = src;
    };
    reader.readAsDataURL(file);
  }, [commitElements, viewportCenter]);

  const onDrop = useCallback((event: ReactDragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = Array.from(event.dataTransfer.files).find((item) => item.type.startsWith('image/'));
    if (file) importImage(file, eventToBoardPoint(event.clientX, event.clientY));
  }, [eventToBoardPoint, importImage]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target)) return;
      const command = event.ctrlKey || event.metaKey;
      if (event.code === 'Space') {
        event.preventDefault();
        spaceHeldRef.current = true;
        setSpaceActive(true);
        return;
      }
      if (command && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) redo(); else undo();
        return;
      }
      if (command && event.key.toLowerCase() === 'y') { event.preventDefault(); redo(); return; }
      if (command && event.key.toLowerCase() === 'c') { event.preventDefault(); copySelected(); return; }
      if (command && event.key.toLowerCase() === 'v') { event.preventDefault(); pasteInternal(); return; }
      if (command && event.key.toLowerCase() === 'd') { event.preventDefault(); duplicateSelected(); return; }
      if (command && event.key.toLowerCase() === 'a') { event.preventDefault(); setSelectedIds(elementsRef.current.map((element) => element.id)); return; }
      if (command && (event.key === '+' || event.key === '=')) { event.preventDefault(); zoomAtCenter(1.15); return; }
      if (command && event.key === '-') { event.preventDefault(); zoomAtCenter(1 / 1.15); return; }
      if (event.key === 'Delete' || event.key === 'Backspace') { event.preventDefault(); deleteSelected(); return; }
      if (event.key === 'Escape') { setSelectedIds([]); setTool('select'); hidePanels(); return; }
      if (event.key === 'Enter' && selectedIds.length === 1) { event.preventDefault(); beginTextEdit(selectedIds[0]); return; }
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key) && selectedIds.length) {
        event.preventDefault();
        const amount = event.shiftKey ? 10 : 1;
        const dx = event.key === 'ArrowLeft' ? -amount : event.key === 'ArrowRight' ? amount : 0;
        const dy = event.key === 'ArrowUp' ? -amount : event.key === 'ArrowDown' ? amount : 0;
        const before = cloneElements(elementsRef.current);
        const ids = new Set(selectedIds);
        commitElements(before.map((element) => ids.has(element.id) ? moveElement(element, dx, dy) : element), before);
        return;
      }
      const shortcutMap: Record<string, WhiteboardTool> = { v: 'select', h: 'hand', p: 'pen', k: 'highlighter', e: 'eraser', t: 'text', s: 'sticky', r: 'rectangle', o: 'ellipse', d: 'diamond', l: 'arrow', f: 'frame' };
      const shortcutTool = shortcutMap[event.key.toLowerCase()];
      if (shortcutTool) { event.preventDefault(); chooseTool(shortcutTool); }
    };
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.code === 'Space') {
        spaceHeldRef.current = false;
        setSpaceActive(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [beginTextEdit, chooseTool, commitElements, copySelected, deleteSelected, duplicateSelected, hidePanels, pasteInternal, redo, selectedIds, undo, zoomAtCenter]);

  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      if (isTypingTarget(event.target)) return;
      const imageItem = Array.from(event.clipboardData?.items || []).find((item) => item.type.startsWith('image/'));
      if (imageItem) {
        const file = imageItem.getAsFile();
        if (file) { event.preventDefault(); importImage(file); }
        return;
      }
      const text = event.clipboardData?.getData('text/plain')?.trim();
      if (text && !internalClipboardRef.current.length) {
        event.preventDefault();
        const center = viewportCenter();
        const before = cloneElements(elementsRef.current);
        const created = createElement('text', center.x - 120, center.y - 30, { width: 320, height: 100, text, stroke: draftStyle.stroke });
        commitElements([...before, created], before);
        setSelectedIds([created.id]);
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [commitElements, draftStyle.stroke, importImage, viewportCenter]);

  const makeExportSvg = useCallback(() => {
    const source = svgRef.current;
    if (!source) return null;
    const clone = source.cloneNode(true) as SVGSVGElement;
    clone.querySelectorAll('[data-ui-only="true"]').forEach((node) => node.remove());
    const layer = clone.querySelector('[data-board-layer="true"]') as SVGGElement | null;
    if (!layer) return null;
    layer.removeAttribute('transform');
    const contentBounds = getElementsBounds(elementsRef.current);
    const rect = source.getBoundingClientRect();
    const fallback: Bounds = {
      x: (-viewRef.current.x) / viewRef.current.scale,
      y: (-viewRef.current.y) / viewRef.current.scale,
      width: rect.width / viewRef.current.scale,
      height: rect.height / viewRef.current.scale,
    };
    const bounds = contentBounds || fallback;
    const padding = 60;
    const exportBounds = { x: bounds.x - padding, y: bounds.y - padding, width: bounds.width + padding * 2, height: bounds.height + padding * 2 };
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    clone.setAttribute('viewBox', `${exportBounds.x} ${exportBounds.y} ${exportBounds.width} ${exportBounds.height}`);
    clone.setAttribute('width', String(exportBounds.width));
    clone.setAttribute('height', String(exportBounds.height));
    clone.removeAttribute('class');
    clone.removeAttribute('style');
    const background = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    background.setAttribute('x', String(exportBounds.x));
    background.setAttribute('y', String(exportBounds.y));
    background.setAttribute('width', String(exportBounds.width));
    background.setAttribute('height', String(exportBounds.height));
    background.setAttribute('fill', '#fbfcfe');
    clone.insertBefore(background, layer);
    return { xml: new XMLSerializer().serializeToString(clone), bounds: exportBounds };
  }, []);

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const safeFilename = `${title.trim() || '自由画板'}`.replace(/[\\/:*?"<>|]/g, '-');

  const exportSvg = useCallback(() => {
    const exported = makeExportSvg();
    if (!exported) return;
    downloadBlob(new Blob([exported.xml], { type: 'image/svg+xml;charset=utf-8' }), `${safeFilename}.svg`);
    setExportOpen(false);
  }, [makeExportSvg, safeFilename]);

  const exportPng = useCallback(() => {
    const exported = makeExportSvg();
    if (!exported) return;
    const blob = new Blob([exported.xml], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const image = new window.Image();
    image.onload = () => {
      const longest = Math.max(exported.bounds.width, exported.bounds.height);
      const ratio = Math.min(2, 4096 / Math.max(1, longest));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(exported.bounds.width * ratio));
      canvas.height = Math.max(1, Math.round(exported.bounds.height * ratio));
      const context = canvas.getContext('2d');
      if (!context) return;
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((png) => { if (png) downloadBlob(png, `${safeFilename}.png`); }, 'image/png');
      URL.revokeObjectURL(url);
    };
    image.src = url;
    setExportOpen(false);
  }, [makeExportSvg, safeFilename]);

  const exportBackup = useCallback(() => {
    const backup = {
      version: 1,
      exportedAt: new Date().toISOString(),
      board: { id: boardId, title, elements: elementsRef.current, view: viewRef.current, updatedAt: Date.now() },
    };
    downloadBlob(new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' }), `${safeFilename}.json`);
    setExportOpen(false);
  }, [boardId, safeFilename, title]);

  const importBackup = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result || '{}'));
        const board = parsed.board || parsed;
        if (!Array.isArray(board.elements)) throw new Error('invalid board');
        const before = cloneElements(elementsRef.current);
        setTitle(board.title || '导入的画板');
        setViewNow(board.view || { x: 0, y: 0, scale: 1 });
        commitElements(cloneElements(board.elements), before);
        setSelectedIds([]);
        setWelcomeDismissed(true);
      } catch {
        window.alert('无法导入：请选择由自由画板导出的 JSON 备份文件。');
      }
    };
    reader.readAsText(file);
  }, [commitElements, setViewNow]);

  const toggleFullscreen = useCallback(async () => {
    if (document.fullscreenElement) await document.exitFullscreen();
    else await pageRef.current?.requestFullscreen();
  }, []);

  const editingElement = editingId ? elements.find((element) => element.id === editingId) : undefined;
  const editorStyle = editingElement ? {
    left: `${view.x + editingElement.x * view.scale}px`,
    top: `${view.y + (editingElement.kind === 'frame' ? editingElement.y - 42 : editingElement.y) * view.scale}px`,
    width: `${Math.max(90, editingElement.width * view.scale)}px`,
    height: `${Math.max(42, (editingElement.kind === 'frame' ? 42 : editingElement.height) * view.scale)}px`,
    fontSize: `${Math.max(12, editingElement.fontSize * view.scale)}px`,
    color: editingElement.stroke,
    padding: editingElement.kind === 'sticky' ? `${16 * view.scale}px` : `${8 * view.scale}px`,
    textAlign: editingElement.kind === 'text' || editingElement.kind === 'sticky' || editingElement.kind === 'frame' ? 'left' : 'center',
  } as CSSProperties : undefined;

  const gridStyle = {
    '--wb-grid-size': `${24 * view.scale}px`,
    '--wb-grid-x': `${view.x}px`,
    '--wb-grid-y': `${view.y}px`,
  } as CSSProperties;

  const timerLabel = `${String(Math.floor(timerSeconds / 60)).padStart(2, '0')}:${String(timerSeconds % 60).padStart(2, '0')}`;
  const singleSelection = selectedElements.length === 1 ? selectedElements[0] : undefined;
  const selectionScale = 1 / view.scale;

  return (
    <div className="wb-page df-dark-page" ref={pageRef} onDragOver={(event) => event.preventDefault()} onDrop={onDrop}>
      <header className="wb-topbar">
        <div className="wb-topbar-left">
          <button
            type="button"
            className={`wb-board-switcher ${boardMenuOpen ? 'is-open' : ''}`}
            onClick={() => { hidePanels(); setBoardMenuOpen((open) => !open); }}
            aria-label="切换画板"
          >
            <span className="wb-board-mark"><GatewayOutlined /></span>
            <span className="wb-board-copy"><strong>自由画板</strong><small>{boards.length} 个画板</small></span>
            <span className="wb-chevron">⌄</span>
          </button>
          <div className="wb-title-box">
            <input value={title} onChange={(event) => setTitle(event.target.value)} onBlur={() => { if (!title.trim()) setTitle('未命名画板'); }} aria-label="画板名称" />
            <span className={`wb-save-state ${saveStatus}`}><SaveOutlined /> {saveStatus === 'saving' ? '保存中' : '已自动保存'}</span>
          </div>
        </div>

        <div className="wb-history-actions" aria-label="历史操作">
          <button type="button" onClick={undo} disabled={!undoStack.length} title="撤销 (Ctrl+Z)"><UndoOutlined /></button>
          <button type="button" onClick={redo} disabled={!redoStack.length} title="重做 (Ctrl+Shift+Z)"><RedoOutlined /></button>
        </div>

        <div className="wb-topbar-actions">
          <button type="button" className={`wb-action-chip ${timerRunning ? 'is-running' : ''} ${timerSeconds === 0 ? 'is-done' : ''}`} onClick={() => { hidePanels(); setTimerOpen((open) => !open); }}>
            <ClockCircleOutlined /><span>{timerSeconds === 0 ? '时间到' : timerLabel}</span>
          </button>
          <button type="button" className="wb-action-chip" onClick={() => { hidePanels(); setTemplateOpen((open) => !open); }}><AppstoreOutlined /><span>模板</span></button>
          <button type="button" className="wb-action-chip wb-export-trigger" onClick={() => { hidePanels(); setExportOpen((open) => !open); }}><DownloadOutlined /><span>导出</span></button>
          <button type="button" className="wb-icon-action" onClick={toggleFullscreen} title={isFullscreen ? '退出全屏' : '进入全屏'}>{isFullscreen ? <CompressOutlined /> : <ExpandOutlined />}</button>
        </div>

        {boardMenuOpen && (
          <div className="wb-popover wb-board-menu">
            <div className="wb-popover-head"><div><strong>我的画板</strong><span>内容仅保存在当前浏览器</span></div><button type="button" onClick={createNewBoard}><PlusOutlined /> 新建</button></div>
            <div className="wb-board-list">
              {[...boards].sort((a, b) => b.updatedAt - a.updatedAt).map((board) => (
                <div key={board.id} className={`wb-board-item ${board.id === boardId ? 'is-current' : ''}`}>
                  <button type="button" className="wb-board-item-main" onClick={() => switchBoard(board.id)}>
                    <span className="wb-board-thumb"><BorderOutlined /></span>
                    <span><strong>{board.title}</strong><small>{board.elements.length} 个元素 · {new Date(board.updatedAt).toLocaleDateString('zh-CN')}</small></span>
                  </button>
                  <button type="button" className="wb-board-delete" onClick={() => deleteBoard(board.id)} title="删除画板"><DeleteOutlined /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {templateOpen && (
          <div className="wb-popover wb-template-menu">
            <div className="wb-popover-title"><strong>快速开始</strong><span>模板会添加到当前视野中心，可继续自由编辑</span></div>
            <div className="wb-template-grid">
              {TEMPLATE_DEFINITIONS.map((template) => (
                <button key={template.id} type="button" onClick={() => insertTemplate(template.id)}>
                  <span className="wb-template-icon" style={{ background: template.color }}>{template.icon}</span>
                  <span><strong>{template.name}</strong><small>{template.desc}</small></span>
                </button>
              ))}
            </div>
          </div>
        )}

        {timerOpen && (
          <div className="wb-popover wb-timer-menu">
            <div className="wb-timer-display">{timerLabel}</div>
            <div className="wb-timer-presets">
              {[5, 10, 15, 25].map((minutes) => <button type="button" key={minutes} onClick={() => { setTimerSeconds(minutes * 60); setTimerRunning(false); }}>{minutes} 分钟</button>)}
            </div>
            <div className="wb-timer-controls">
              <button type="button" className="is-primary" onClick={() => { if (timerSeconds === 0) setTimerSeconds(5 * 60); setTimerRunning((running) => !running); }}>{timerRunning ? '暂停' : '开始计时'}</button>
              <button type="button" onClick={() => { setTimerRunning(false); setTimerSeconds(5 * 60); }}>重置</button>
            </div>
          </div>
        )}

        {exportOpen && (
          <div className="wb-popover wb-export-menu">
            <button type="button" onClick={exportPng}><PictureOutlined /><span><strong>导出 PNG 图片</strong><small>适合发群和插入文档</small></span></button>
            <button type="button" onClick={exportSvg}><DownloadOutlined /><span><strong>导出 SVG 矢量图</strong><small>放大依然清晰</small></span></button>
            <button type="button" onClick={exportBackup}><SaveOutlined /><span><strong>备份可编辑画板</strong><small>导出 JSON，稍后可恢复</small></span></button>
            <button type="button" onClick={() => backupInputRef.current?.click()}><UploadOutlined /><span><strong>恢复画板备份</strong><small>导入自由画板 JSON 文件</small></span></button>
          </div>
        )}
      </header>

      <div className="wb-workspace" ref={workspaceRef}>
        <svg
          ref={svgRef}
          className={`wb-canvas is-tool-${spaceActive ? 'hand' : tool} ${gridVisible ? 'has-grid' : ''}`}
          style={gridStyle}
          role="application"
          aria-label="自由画板绘图区"
          tabIndex={0}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={finishPointerInteraction}
          onPointerCancel={finishPointerInteraction}
          onDoubleClick={onDoubleClick}
          onWheel={onWheel}
        >
          <defs>
            <filter id="wb-soft-shadow" x="-30%" y="-30%" width="160%" height="170%">
              <feDropShadow dx="0" dy="5" stdDeviation="6" floodColor="#0f172a" floodOpacity=".14" />
            </filter>
          </defs>
          <g data-board-layer="true" transform={`translate(${view.x} ${view.y}) scale(${view.scale})`}>
            {elements.map((element) => <BoardElementView key={element.id} element={element} allElements={elements} />)}
          </g>

          <g data-ui-only="true" transform={`translate(${view.x} ${view.y}) scale(${view.scale})`} className="wb-selection-layer">
            {selectedBounds && (
              <rect
                className="wb-selection-box"
                x={selectedBounds.x - 5 * selectionScale}
                y={selectedBounds.y - 5 * selectionScale}
                width={selectedBounds.width + 10 * selectionScale}
                height={selectedBounds.height + 10 * selectionScale}
                rx={7 * selectionScale}
                strokeWidth={1.6 * selectionScale}
              />
            )}
            {singleSelection && singleSelection.kind === 'arrow' && (() => {
              const points = getConnectorPoints(singleSelection, elements);
              return (
                <>
                  <circle className="wb-resize-handle" cx={points.start.x} cy={points.start.y} r={6 * selectionScale} strokeWidth={2 * selectionScale} onPointerDown={(event) => startConnectorEndpoint(event, singleSelection, 'start')} />
                  <circle className="wb-resize-handle" cx={points.end.x} cy={points.end.y} r={6 * selectionScale} strokeWidth={2 * selectionScale} onPointerDown={(event) => startConnectorEndpoint(event, singleSelection, 'end')} />
                </>
              );
            })()}
            {singleSelection && !['arrow', 'path', 'highlighter'].includes(singleSelection.kind) && (() => {
              const bounds = getElementBounds(singleSelection, elements);
              const handles: Array<[ResizeHandle, number, number]> = [
                ['nw', bounds.x, bounds.y], ['ne', bounds.x + bounds.width, bounds.y],
                ['sw', bounds.x, bounds.y + bounds.height], ['se', bounds.x + bounds.width, bounds.y + bounds.height],
              ];
              return handles.map(([handle, x, y]) => (
                <circle key={handle} className="wb-resize-handle" cx={x} cy={y} r={6 * selectionScale} strokeWidth={2 * selectionScale} onPointerDown={(event) => startResize(event, singleSelection, handle)} />
              ));
            })()}
            {marquee && <rect className="wb-marquee" x={marquee.x} y={marquee.y} width={marquee.width} height={marquee.height} strokeWidth={1.3 * selectionScale} />}
          </g>
        </svg>

        <aside className="wb-toolbar" aria-label="画板工具">
          <ToolButton tool="select" current={tool} icon={<SelectOutlined />} onClick={() => chooseTool('select')} />
          <ToolButton tool="hand" current={tool} icon={<DragOutlined />} onClick={() => chooseTool('hand')} />
          <span className="wb-tool-divider" />
          <ToolButton tool="pen" current={tool} icon={<EditOutlined />} onClick={() => chooseTool('pen')} />
          <ToolButton tool="highlighter" current={tool} icon={<HighlightOutlined />} onClick={() => chooseTool('highlighter')} />
          <ToolButton tool="eraser" current={tool} icon={<ScissorOutlined />} onClick={() => chooseTool('eraser')} />
          <span className="wb-tool-divider" />
          <ToolButton tool="text" current={tool} icon={<FontSizeOutlined />} onClick={() => chooseTool('text')} />
          <ToolButton tool="sticky" current={tool} icon={<span className="wb-sticky-tool" />} onClick={() => chooseTool('sticky')} />
          <button type="button" className={`wb-tool-button ${['rectangle', 'ellipse', 'diamond'].includes(tool) ? 'is-active' : ''}`} onClick={() => { setShapeMenuOpen((open) => !open); setColorMenuOpen(false); }} title="图形 (R / O / D)" aria-label="选择图形"><BorderOutlined /><span className="wb-mini-caret">›</span></button>
          <ToolButton tool="arrow" current={tool} icon={<NodeIndexOutlined />} onClick={() => chooseTool('arrow')} />
          <ToolButton tool="frame" current={tool} icon={<ColumnWidthOutlined />} onClick={() => chooseTool('frame')} />
          <button type="button" className="wb-tool-button" onClick={() => imageInputRef.current?.click()} title="插入图片" aria-label="插入图片"><PictureOutlined /></button>
          <span className="wb-tool-divider" />
          <button type="button" className={`wb-tool-button wb-color-tool ${colorMenuOpen ? 'is-active' : ''}`} onClick={() => { setColorMenuOpen((open) => !open); setShapeMenuOpen(false); }} title="颜色与线宽" aria-label="颜色与线宽">
            <BgColorsOutlined /><i style={{ background: draftStyle.stroke }} /><span className="wb-mini-caret">›</span>
          </button>
        </aside>

        {shapeMenuOpen && (
          <div className="wb-float-panel wb-shape-menu">
            <button type="button" className={tool === 'rectangle' ? 'is-active' : ''} onClick={() => chooseTool('rectangle')} title="矩形 (R)"><span className="shape-rect" /></button>
            <button type="button" className={tool === 'ellipse' ? 'is-active' : ''} onClick={() => chooseTool('ellipse')} title="圆形 (O)"><span className="shape-circle" /></button>
            <button type="button" className={tool === 'diamond' ? 'is-active' : ''} onClick={() => chooseTool('diamond')} title="菱形 (D)"><span className="shape-diamond" /></button>
          </div>
        )}

        {colorMenuOpen && (
          <div className="wb-float-panel wb-color-menu">
            <span className="wb-panel-label">线条 / 文字</span>
            <div className="wb-color-row">
              {STROKE_COLORS.map((color) => <button type="button" key={color} className={draftStyle.stroke === color ? 'is-active' : ''} style={{ background: color }} onClick={() => setDraftStyle((style) => ({ ...style, stroke: color }))} aria-label={`选择颜色 ${color}`} />)}
              <label className="wb-custom-color" title="自定义颜色"><input type="color" value={draftStyle.stroke} onChange={(event) => setDraftStyle((style) => ({ ...style, stroke: event.target.value }))} /><PlusOutlined /></label>
            </div>
            <span className="wb-panel-label">线宽</span>
            <div className="wb-width-row">
              {[1, 2, 4, 8].map((width) => <button type="button" key={width} className={draftStyle.strokeWidth === width ? 'is-active' : ''} onClick={() => setDraftStyle((style) => ({ ...style, strokeWidth: width }))}><i style={{ height: Math.min(8, width) }} />{width}</button>)}
            </div>
          </div>
        )}

        {selectedElements.length > 0 && (
          <div className="wb-properties" data-ui-only="true">
            <span className="wb-selection-count">{selectedElements.length > 1 ? `${selectedElements.length} 项` : TOOL_META[(singleSelection?.kind === 'path' ? 'pen' : singleSelection?.kind === 'image' ? 'select' : singleSelection?.kind || 'select') as WhiteboardTool]?.label || '图片'}</span>
            <span className="wb-property-divider" />
            <div className="wb-inline-colors" title="线条或文字颜色">
              {STROKE_COLORS.slice(0, 6).map((color) => <button type="button" key={color} style={{ background: color }} className={selectedElements.every((item) => item.stroke === color) ? 'is-active' : ''} onClick={() => { patchSelected({ stroke: color }); setDraftStyle((style) => ({ ...style, stroke: color })); }} />)}
              <label><input type="color" value={singleSelection?.stroke || draftStyle.stroke} onChange={(event) => patchSelected({ stroke: event.target.value })} /><PlusOutlined /></label>
            </div>
            {selectedElements.some((item) => FILLABLE_KINDS.includes(item.kind)) && (
              <div className="wb-fill-picker">
                <span>填充</span>
                <select value={singleSelection?.fill || '#ffffff'} onChange={(event) => patchSelected({ fill: event.target.value })}>
                  {FILL_COLORS.map((color) => <option key={color} value={color}>{color === 'transparent' ? '透明' : color}</option>)}
                </select>
                <i style={{ background: singleSelection?.fill === 'transparent' ? 'linear-gradient(135deg,#fff 46%,#ef4444 48%,#ef4444 52%,#fff 54%)' : singleSelection?.fill }} />
              </div>
            )}
            <div className="wb-stroke-picker" title="线宽">
              {[1, 2, 4, 8].map((width) => <button type="button" key={width} className={singleSelection?.strokeWidth === width ? 'is-active' : ''} onClick={() => patchSelected({ strokeWidth: width })}><i style={{ height: Math.min(width, 6) }} /></button>)}
            </div>
            {singleSelection && TEXT_KINDS.includes(singleSelection.kind) && (
              <div className="wb-font-size"><button type="button" onClick={() => patchSelected({ fontSize: Math.max(12, singleSelection.fontSize - 2) })}><MinusOutlined /></button><span>{singleSelection.fontSize}</span><button type="button" onClick={() => patchSelected({ fontSize: Math.min(48, singleSelection.fontSize + 2) })}><PlusOutlined /></button></div>
            )}
            <span className="wb-property-divider" />
            <button type="button" onClick={duplicateSelected} title="复制一份 (Ctrl+D)"><CopyOutlined /></button>
            <button type="button" onClick={() => moveSelectionLayer('front')} title="置于顶层">上移</button>
            <button type="button" onClick={() => moveSelectionLayer('back')} title="置于底层">下移</button>
            <button type="button" className="is-danger" onClick={deleteSelected} title="删除"><DeleteOutlined /></button>
          </div>
        )}

        {editingElement && (
          <textarea
            ref={editorRef}
            className={`wb-text-editor is-${editingElement.kind}`}
            style={editorStyle}
            value={editingElement.text || ''}
            placeholder={editingElement.kind === 'sticky' ? '输入想法…' : editingElement.kind === 'frame' ? '讨论区名称' : '输入文字'}
            onChange={(event) => setElementsNow(elementsRef.current.map((element) => element.id === editingElement.id ? { ...element, text: event.target.value } : element))}
            onBlur={() => finishTextEdit()}
            onKeyDown={(event) => {
              if (event.key === 'Escape') { event.preventDefault(); finishTextEdit(true); }
              if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) { event.preventDefault(); editorRef.current?.blur(); }
            }}
          />
        )}

        {elements.length === 0 && !welcomeDismissed && (
          <div className="wb-welcome">
            <span className="wb-welcome-icon"><EditOutlined /></span>
            <h2>开始这次讨论</h2>
            <p>双击画布输入文字，也可以从一个常用模板开始</p>
            <div className="wb-welcome-actions">
              <button type="button" className="is-primary" onClick={() => setWelcomeDismissed(true)}>开始空白画板</button>
              <button type="button" onClick={() => insertTemplate('architecture')}>系统架构图</button>
              <button type="button" onClick={() => insertTemplate('brainstorm')}>头脑风暴</button>
            </div>
            <span className="wb-welcome-tip">小提示：按住空格拖动画布，Ctrl + 滚轮缩放</span>
          </div>
        )}

        <div className="wb-canvas-status">
          <span>{TOOL_META[spaceActive ? 'hand' : tool].label}</span>
          <em>·</em>
          <span>{elements.length} 个元素</span>
        </div>

        <div className="wb-zoom-controls">
          <button type="button" onClick={() => zoomAtCenter(1 / 1.15)} title="缩小"><MinusOutlined /></button>
          <button type="button" className="wb-zoom-value" onClick={() => setViewNow({ ...viewRef.current, scale: 1 })} title="恢复 100%">{Math.round(view.scale * 100)}%</button>
          <button type="button" onClick={() => zoomAtCenter(1.15)} title="放大"><PlusOutlined /></button>
          <span />
          <button type="button" onClick={fitToContent} title="适应内容"><AimOutlined /></button>
          <button type="button" className={gridVisible ? 'is-active' : ''} onClick={() => setGridVisible((visible) => !visible)} title="显示/隐藏网格"><span className="wb-grid-icon" /></button>
        </div>

        <input ref={imageInputRef} type="file" accept="image/*" hidden onChange={(event) => { const file = event.target.files?.[0]; if (file) importImage(file); event.target.value = ''; }} />
        <input ref={backupInputRef} type="file" accept="application/json,.json" hidden onChange={(event) => { const file = event.target.files?.[0]; if (file) importBackup(file); event.target.value = ''; }} />
      </div>
    </div>
  );
}
